// MUST be first — installs Array/String.prototype.at + Object.hasOwn
// shims for iOS 15.0–15.3 WebViews before any dependency's module code
// runs (Sentry TRYBEUP-CAPACITOR-7). See lib/polyfills.ts.
import './lib/polyfills'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { GoogleAuth } from '@southdevs/capacitor-google-auth'
import './index.css'
import App from './App'
import { initOfflineSync } from './lib/offlineSync'
import { setupOfflineSync as setupOfflineQueue } from './lib/offlineQueue'
import { initErrorTracking } from './lib/errorTracking'
import { selfHealChunkReload, CHUNK_ERROR_RE } from './lib/chunkReload'
import { initSentry } from './lib/sentry'
import { configureRevenueCat } from './lib/revenuecat'
import { detectAudioCapability } from './lib/audioCapability'
import { markBoot } from './lib/bootTiming'
import { captureCoachReferralFromUrl } from './lib/coachReferralCapture'
import { captureUtmFromUrl } from './lib/utmCapture'
import { optionalChunkLoading } from './lib/optionalChunk'

// First boot milestone — the JS bundle has parsed and our entry is
// executing. Marks are ms-since-WebView-document-start. See
// lib/bootTiming.ts; useAuth fires the PostHog `boot_timing` event when
// the auth gate clears.
markBoot('script_start')

// Capture a coach referral code (?coach=CODE) at the earliest possible moment,
// for logged-out visitors too — a coach's invite link is opened by brand-new
// people before the authenticated app mounts, so capturing here (not only in a
// post-auth hook) is what keeps the referral attribution intact through signup.
captureCoachReferralFromUrl()
// Stash first-touch campaign attribution (utm_*) for logged-out landing
// visitors before React mounts, so signup → activation can be traced back to
// the channel + the specific video. Must run BEFORE we'd ever strip the query.
captureUtmFromUrl()

// Sentry first — wraps every later init in error capture. No-op until
// the founder provisions VITE_SENTRY_DSN in the build env.
initSentry()
initOfflineSync()
// Also init the lighter offlineQueue used by habit-log and quick-workout-log
// flows so queued actions flush as soon as the app is online, regardless of
// which tab the user lands on first.
setupOfflineQueue()
initErrorTracking()

// Background-resume recovery — invalidate stale queries after a
// 1-5min background, hard-reload after a 5min+ background. iOS
// WebView snapshots get unreliable after a long background and we
// were hitting "black screen on MyDay, only fixed by refresh"
// (founder report 2026-06-17). See lib/appResumeRefresh.ts.
import('./lib/appResumeRefresh').then(({ initAppResumeRefresh }) => initAppResumeRefresh()).catch(() => {})

// Self-heal stale-deploy chunk errors. When a deploy ships while a tab is
// open, lazy-loading a route can fail — the old page asking for a chunk hash
// that no longer exists. Vite fires `vite:preloadError` for exactly this;
// reloading fetches the fresh index.html → new chunk map → fixed. The shared
// helper's sessionStorage guard caps this at one auto-reload per minute across
// ALL paths (these window listeners + the ErrorBoundary render path), after
// which the error surfaces normally. See lib/chunkReload.ts.
window.addEventListener('vite:preloadError', (event) => {
  // An optional chunk (analytics, Sentry replay) failing must not reload the
  // page — let its own import fail quietly (lib/optionalChunk.ts).
  if (optionalChunkLoading()) { event.preventDefault(); return }
  if (selfHealChunkReload()) event.preventDefault()
})
// Backstop: a failed dynamic import does NOT always surface as
// `vite:preloadError`. On WebKit / iOS it often arrives as a plain unhandled
// rejection with one of these messages instead — which is the crash that
// reached a stale-bundle device (Sentry "Importing a module script failed",
// build 1.1.221 · ios). Match those by message and apply the same
// one-reload-per-minute self-heal so the device fetches a fresh bundle and
// recovers, rather than hard-crashing.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { message?: string } | string | undefined
  const msg = typeof reason === 'string' ? reason : (reason?.message ?? '')
  if (CHUNK_ERROR_RE.test(msg) && selfHealChunkReload()) {
    event.preventDefault()
  }
})
// Second backstop: some WebKit / iOS builds surface a failed module import as
// a plain global `error` event ("Importing a module script failed.") — NOT an
// unhandledrejection and NOT vite:preloadError, so it slipped past both
// listeners above and hard-crashed (Sentry "Importing a module script failed",
// build 1.1.471 · ios, capacitor://localhost). Apply the same one-reload-per-
// minute self-heal so the device fetches a fresh bundle and recovers.
window.addEventListener('error', (event) => {
  const errMsg = (event as ErrorEvent)?.message ?? ''
  const nestedMsg = ((event as ErrorEvent)?.error as { message?: string } | undefined)?.message ?? ''
  const msg = `${errMsg} ${nestedMsg}`
  if (CHUNK_ERROR_RE.test(msg) && selfHealChunkReload()) {
    event.preventDefault()
  }
})

// Detect whether this native build has the AVAudioSession `.ambient`
// configuration (iOS build ≥ 17). Timer code calls canPlayMixedAudio()
// synchronously to decide whether to fire the beep — if this hasn't
// resolved yet, beeps stay haptics-only (safe default). Web is always
// `true`. Fire-and-forget; failure leaves the cache as "not safe", so
// the worst case is haptics-only behaviour we already shipped.
detectAudioCapability().catch(() => { /* logged inside helper */ })

// Native-only: configure RevenueCat for App Store IAP. No-op on web.
// Identity is set later by useAuth's onAuthStateChange (SIGNED_IN →
// Purchases.logIn). See apps/web/src/lib/revenuecat.ts for details.
configureRevenueCat().catch(() => { /* logged inside the helper */ })

// iOS status-bar config — explicit setOverlaysWebView(true) so the
// WebView extends UNDER the status bar (Dynamic Island / clock /
// signal icons render on top of the app's amber gradient header).
// This is the Capacitor 7 default but stating it explicitly removes
// any ambiguity from project-config drift, and ensures
// env(safe-area-inset-top) returns the actual notch height in CSS
// (which our header / sticky-headers rely on for clearance).
//
// The icon STYLE must match the theme the app is about to render in, not a
// hardcoded one. The default/stored theme can be LIGHT (cream) — on which
// Style.Dark (white icons) is invisible (Android QA v1.1.599, Issue #2:
// "status bar icons not visible against the background"). useTheme owns the
// per-theme style once React mounts; we seed the SAME choice here from the
// stored theme (key mirrors useTheme's THEME_KEY) so there's no white-on-
// cream flash before the effect runs. light → Style.Light (dark icons);
// dark/calm/modern → Style.Dark (light icons).
//
// Wrapped in a try/catch + isNativePlatform check so any native-only
// throw on web is silenced (web is a no-op for the StatusBar plugin).
if (Capacitor.isNativePlatform()) {
  let _initialStatusBarStyle = Style.Dark;
  try {
    _initialStatusBarStyle =
      window.localStorage?.getItem("trybeup_theme_v2") === "light" ? Style.Light : Style.Dark;
  } catch {
    /* storage may be unavailable — fall back to Dark */
  }
  StatusBar.setOverlaysWebView({ overlay: true })
    .catch(() => { /* no-op on platforms where StatusBar isn't available */ })
  StatusBar.setStyle({ style: _initialStatusBarStyle })
    .catch(() => { /* no-op */ })
}

// Native-only: initialise the Google Sign-In SDK so `GoogleAuth.signIn()`
// can present the system Google sign-in sheet without a Safari hop.
// On web we keep the existing GoTrue redirect-based PKCE flow — calling
// `initialize` in a browser would just no-op, but we guard anyway to
// avoid pulling in any native-only side effects on the web bundle.
//
// Bundle ID `com.trybeup.app` — must match the iOS OAuth Client created
// in Google Cloud Console. The reversed client ID is also wired up as a
// CFBundleURLTypes entry in `apps/mobile/ios/App/App/Info.plist`.
// IMPORTANT: gate this to iOS only. The native Google Sign-In SDK is wired up
// on iOS but NOT on Android — as of builds ≥41 the plugin IS in the Android
// project (comment refreshed 2026-08-04), but there's still no Android OAuth
// client / androidClientId config, so initialize() has nothing valid to
// initialise with. Historically it rejected with
// "GoogleAuth plugin is not implemented on android"; because the call wasn't
// awaited or caught, that rejection surfaced as an unhandled-rejection crash
// on EVERY Android launch (Sentry TRYBEUP-CAPACITOR-9, v1.1.453/1.1.454 — it
// fired at startup, NOT on the sign-in button). Restricting to iOS stops it at
// the source; the trailing .catch is belt-and-braces so a native init failure
// can never bubble up as an unhandled rejection again. (2026-06-28)
if (Capacitor.getPlatform() === 'android') {
  // ANDROID (2026-08-05): the native side reads androidClientId from
  // capacitor.config.ts; initialize() with no options applies that config.
  // On builds older than 45 the config isn't baked in and sign-in fails at
  // tap time — LoginPage catches that and asks the user to update.
  Promise.resolve(GoogleAuth.initialize({ scopes: ['profile', 'email'], grantOfflineAccess: false }))
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.warn('[auth] GoogleAuth.initialize (android) failed:', e)
    })
}
if (Capacitor.getPlatform() === 'ios') {
  const iosClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID
  if (iosClientId) {
    Promise.resolve(
      GoogleAuth.initialize({
        clientId: iosClientId,
        scopes: ['profile', 'email'],
        grantOfflineAccess: false,
      }),
    ).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn('[auth] GoogleAuth.initialize failed:', e)
    })
  } else {
    // Don't crash the app — surface in the console so a missing build env
    // var is obvious during dev/TestFlight, but email+password still works.
    // eslint-disable-next-line no-console
    console.warn('[auth] VITE_GOOGLE_IOS_CLIENT_ID not set — native Google sign-in disabled.')
  }
}

// Lazy-load PostHog so posthog-js (~60 kB gzipped) lives in its
// own chunk and doesn't bloat the initial page load. Fire-and-forget.
import('./lib/posthog').then(({ initPostHog }) => initPostHog()).catch(() => {})

const root = document.getElementById('root')
if (!root) throw new Error('Root element #root not found in index.html')

markBoot('react_render')
createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>
)

// Native-only. Capgo's notifyAppReady() — "this bundle booted, don't roll it
// back" (no call within ~10s of launch = rollback) — is NOT sent from here
// any more: a setTimeout(0) after render() fired before React had rendered
// anything, so a bundle that crashed while rendering still reported healthy
// and was never rolled back (review 2026-10-17). <AppReadySignal />
// (lib/appReady.ts) sends it from inside the top-level ErrorBoundary once
// the app shell has rendered without crashing.
if (Capacitor.isNativePlatform()) {
  // Apply downloaded Capgo bundles when the user backgrounds → foregrounds
  // the app, so high-iteration releases reach users without the
  // "close and reopen twice" dance. See lib/seamlessUpdate.ts.
  import('./lib/seamlessUpdate')
    .then(({ initSeamlessCapgoUpdates }) => initSeamlessCapgoUpdates())
    .catch(() => { /* no-op on web preview / plugin missing */ })

  // ANDROID hardware back button. Without a JS listener, Capacitor's default
  // walks WebView history and then — at the root — does NOTHING: back is
  // swallowed, the app neither navigates nor minimises (the classic Play
  // review complaint). With this listener: in-app history pops (which also
  // closes our overlays, since tab changes are pushState entries), and at
  // the true root the app minimises like every other Android app.
  // iOS has no hardware back; the listener simply never fires there.
  // isPluginAvailable FIRST — the same rule the camera bug taught this
  // morning (useCameraPicker.ts): OTA bundles run inside native shells of
  // ANY age, and a June-era Android build has the updater but NOT the App
  // plugin. Without this check, addListener rejects with '"App" plugin is
  // not implemented on android' on every launch of those devices — which
  // is precisely the crash-alert this guard's first version caused (1.2.10,
  // 2026-08-04, ~1 hour after shipping). The .catch stays as the belt to
  // this suspender: any variant of the failure degrades to the platform
  // default instead of a crash report.
  if (Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('App')) {
    import('@capacitor/app')
      .then(({ App: CapApp }) =>
        CapApp.addListener('backButton', ({ canGoBack }) => {
          // Radix dialogs/sheets have NO history integration — they close on
          // Escape, which the hardware back button doesn't generate. Without
          // this, back navigated the page UNDERNEATH the open modal. If a
          // Radix layer is open, synthesize a CANCELABLE Escape (Radix listens
          // at document level, capture phase, and does not check isTrusted).
          //
          // Three behaviours, and BOTH extremes were shipped as bugs — do not
          // "simplify" toward either:
          //   • Unconditional return → a DEAD back key for layers that block
          //     their own dismissal (2026-08-06 sweep-4 regression).
          //   • Fall through to history.back() on a refused dismissal → back
          //     NAVIGATES THE ROUTE UNDERNEATH the still-open modal, defeating
          //     the modal's own protection (2026-08-06 sweep-6 regression).
          // Correct: some layers block dismissal via onEscapeKeyDown →
          // preventDefault (ProfileGate, FirstSessionGuide for a new user,
          // HabitFormModal while saving). If the layer ACCEPTED the Escape
          // (defaultPrevented false) we consume the press; if it REFUSED, we
          // background the app (minimizeApp) — never navigate under it. Normal
          // back-nav (no layer open) is handled below, untouched.
          const openLayer = document.querySelector(
            '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
          )
          if (openLayer) {
            const esc = new KeyboardEvent('keydown', {
              key: 'Escape', code: 'Escape', bubbles: true, cancelable: true,
            })
            document.dispatchEvent(esc)
            if (!esc.defaultPrevented) return  // layer closed → consume the press
            // Layer refused to close (ProfileGate, FirstSessionGuide, a saving
            // HabitFormModal). Do NOT fall through to history.back() — that
            // navigates the route UNDERNEATH the still-open modal, defeating the
            // modal's own protection (regression introduced 2026-08-06, caught
            // same day). Background the app instead: never a dead key (the
            // sweep-4 bug), never a route change beneath a blocking modal.
            CapApp.minimizeApp().catch(() => CapApp.exitApp())
            return
          }
          if (canGoBack) window.history.back()
          else CapApp.minimizeApp().catch(() => CapApp.exitApp())
        }),
      )
      .catch(() => { /* plugin missing/stale shell — default behaviour stands */ })
  }
}
